import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import {
  startOfDay,
  endOfDay,
  subWeeks,
  subMonths,
  subYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format,
  differenceInDays,
  getDaysInMonth,
} from "date-fns";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";

export type CompareMode = "week_vs_week" | "day_of_week" | "month_vs_month" | "year_vs_year";

export interface ComparativeMetrics {
  agendados: number;
  confirmados: number;
  finalizados: number;
  pagos: number;
  receita: number;
}

export interface ComparativeResult {
  periodALabel: string;
  periodBLabel: string;
  periodA: ComparativeMetrics;
  periodB: ComparativeMetrics;
  diffAgendados: number;
  diffConfirmados: number;
  diffFinalizados: number;
  diffPagos: number;
  diffReceita: number;
  pctAgendados: number;
  pctConfirmados: number;
  pctFinalizados: number;
  pctPagos: number;
  pctReceita: number;
}

type AppointmentFinancialSelect = { status?: string | null } | Array<{ status?: string | null }> | null | undefined;

const extractFinancialStatus = (financial: AppointmentFinancialSelect): string | null => {
  if (!financial) return null;
  if (Array.isArray(financial)) return financial[0]?.status ?? null;
  return financial.status ?? null;
};

function funnelCounts(
  status: string,
  financialStatus: string | null,
): { agendados: number; confirmados: number; finalizados: number; pagos: number } {
  if (status === "cancelado" || status === "faltou") {
    return { agendados: 0, confirmados: 0, finalizados: 0, pagos: 0 };
  }
  const agendados = 1;
  const normalizedStatus = status === "pago" ? "finalizado" : status;
  const confirmados = ["confirmado", "em_atendimento", "finalizado"].includes(normalizedStatus) ? 1 : 0;
  const finalizados = normalizedStatus === "finalizado" ? 1 : 0;
  const pagos = deriveFinancialStatusWithLegacyFallback(
    financialStatus ? { status: financialStatus } : null,
    { status, grooming_status: null },
  ) === "pago" ? 1 : 0;
  return { agendados, confirmados, finalizados, pagos };
}

export interface PredictiveData {
  monthProjectedRevenue: number;
  monthRevenueSoFar: number;
  daysElapsed: number;
  daysInMonth: number;
  appointmentsTrend: { date: string; count: number; movingAvg: number }[];
}

export interface ReportsComparativesData {
  comparative: ComparativeResult | null;
  predictive: PredictiveData | null;
}

async function getMetricsForRange(
  clinicId: string,
  fromIso: string,
  toIso: string
): Promise<ComparativeMetrics> {
  const [appointmentsRes, salesRes] = await Promise.all([
    supabase
      .from("appointments")
      .select("id, status, appointment_financial:appointment_financials(status)")
      .eq("clinic_id", clinicId)
      .gte("starts_at", fromIso)
      .lte("starts_at", toIso),
    supabase
      .from("sales")
      .select("total")
      .eq("clinic_id", clinicId)
      .eq("status", "PAGA")
      .gte("created_at", fromIso)
      .lte("created_at", toIso),
  ]);

  if (appointmentsRes.error) throw appointmentsRes.error;
  if (salesRes.error) throw salesRes.error;

  const appointments = appointmentsRes.data ?? [];
  const sales = salesRes.data ?? [];

  let agendados = 0;
  let confirmados = 0;
  let finalizados = 0;
  let pagos = 0;
  appointments.forEach((a) => {
    const row = a as unknown as { status?: string | null; appointment_financial?: AppointmentFinancialSelect };
    const f = funnelCounts(row.status ?? "", extractFinancialStatus(row.appointment_financial));
    agendados += f.agendados;
    confirmados += f.confirmados;
    finalizados += f.finalizados;
    pagos += f.pagos;
  });
  const receita = sales.reduce((sum, s) => sum + Number(s.total ?? 0), 0);

  return { agendados, confirmados, finalizados, pagos, receita };
}

export function useReportsComparatives(
  dateFrom: Date | null,
  dateTo: Date | null,
  compareMode: CompareMode
) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: [
      "reports-comparatives",
      clinicId,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      compareMode,
    ],
    queryFn: async (): Promise<ReportsComparativesData> => {
      if (!clinicId || !dateFrom || !dateTo) {
        return { comparative: null, predictive: null };
      }

      let periodAStart: Date;
      let periodAEnd: Date;
      let periodBStart: Date;
      let periodBEnd: Date;
      let periodALabel: string;
      let periodBLabel: string;

      const now = new Date();

      if (compareMode === "week_vs_week") {
        periodBStart = startOfWeek(now, { weekStartsOn: 0 });
        periodBEnd = endOfDay(now);
        periodAStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 0 });
        periodAEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 0 });
        periodALabel = "Semana passada";
        periodBLabel = "Esta semana";
      } else if (compareMode === "day_of_week") {
        const lastWeekSameDay = subWeeks(now, 1);
        periodAStart = startOfDay(lastWeekSameDay);
        periodAEnd = endOfDay(lastWeekSameDay);
        periodBStart = startOfDay(now);
        periodBEnd = endOfDay(now);
        periodALabel = format(lastWeekSameDay, "dd/MM");
        periodBLabel = format(now, "dd/MM") + " (hoje)";
      } else if (compareMode === "month_vs_month") {
        periodBEnd = endOfDay(now);
        periodBStart = startOfMonth(now);
        periodAEnd = endOfMonth(subMonths(now, 1));
        periodAStart = startOfMonth(subMonths(now, 1));
        periodALabel = format(periodAStart, "MMM yyyy");
        periodBLabel = format(periodBStart, "MMM yyyy") + " (até hoje)";
      } else {
        periodBEnd = endOfDay(now);
        periodBStart = startOfMonth(now);
        const samePeriodLastYear = subYears(now, 1);
        periodAEnd = endOfDay(samePeriodLastYear);
        periodAStart = startOfMonth(samePeriodLastYear);
        periodALabel = format(periodAStart, "MMM yyyy");
        periodBLabel = format(periodBStart, "MMM yyyy") + " (até hoje)";
      }

      const [metricsA, metricsB] = await Promise.all([
        getMetricsForRange(clinicId, periodAStart.toISOString(), periodAEnd.toISOString()),
        getMetricsForRange(clinicId, periodBStart.toISOString(), periodBEnd.toISOString()),
      ]);

      const diffAgendados = metricsB.agendados - metricsA.agendados;
      const diffConfirmados = metricsB.confirmados - metricsA.confirmados;
      const diffFinalizados = metricsB.finalizados - metricsA.finalizados;
      const diffPagos = metricsB.pagos - metricsA.pagos;
      const diffReceita = metricsB.receita - metricsA.receita;

      const pctAgendados = metricsA.agendados !== 0 ? (diffAgendados / metricsA.agendados) * 100 : 0;
      const pctConfirmados = metricsA.confirmados !== 0 ? (diffConfirmados / metricsA.confirmados) * 100 : 0;
      const pctFinalizados = metricsA.finalizados !== 0 ? (diffFinalizados / metricsA.finalizados) * 100 : 0;
      const pctPagos = metricsA.pagos !== 0 ? (diffPagos / metricsA.pagos) * 100 : 0;
      const pctReceita = metricsA.receita !== 0 ? (diffReceita / metricsA.receita) * 100 : 0;

      const comparative: ComparativeResult = {
        periodALabel,
        periodBLabel,
        periodA: metricsA,
        periodB: metricsB,
        diffAgendados,
        diffConfirmados,
        diffFinalizados,
        diffPagos,
        diffReceita,
        pctAgendados,
        pctConfirmados,
        pctFinalizados,
        pctPagos,
        pctReceita,
      };

      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const daysInMonth = getDaysInMonth(now);
      const daysElapsed = differenceInDays(now, monthStart) + 1;

      const { data: salesMonth } = await supabase
        .from("sales")
        .select("total, created_at")
        .eq("clinic_id", clinicId)
        .eq("status", "PAGA")
        .gte("created_at", monthStart.toISOString())
        .lte("created_at", endOfDay(now).toISOString());

      const monthRevenueSoFar = (salesMonth ?? []).reduce((sum, s) => sum + Number(s.total ?? 0), 0);
      const monthProjectedRevenue = daysElapsed > 0 ? (monthRevenueSoFar / daysElapsed) * daysInMonth : 0;

      const trendStart = subMonths(now, 1);
      const { data: appointmentsTrendData } = await supabase
        .from("appointments")
        .select("starts_at")
        .eq("clinic_id", clinicId)
        .gte("starts_at", trendStart.toISOString())
        .lte("starts_at", now.toISOString());

      const byDay: Record<string, number> = {};
      for (let d = new Date(trendStart); d <= now; d.setDate(d.getDate() + 1)) {
        byDay[format(d, "yyyy-MM-dd")] = 0;
      }
      (appointmentsTrendData ?? []).forEach((a) => {
        const key = format(new Date(a.starts_at), "yyyy-MM-dd");
        if (byDay[key] !== undefined) byDay[key] += 1;
      });

      const sortedDates = Object.keys(byDay).sort();
      const windowSize = 7;
      const appointmentsTrend = sortedDates.map((date, i) => {
        const count = byDay[date];
        let movingAvg = count;
        if (i >= windowSize - 1) {
          let sum = 0;
          for (let j = 0; j < windowSize; j++) sum += byDay[sortedDates[i - j]] ?? 0;
          movingAvg = sum / windowSize;
        } else if (i > 0) {
          let sum = 0;
          for (let j = 0; j <= i; j++) sum += byDay[sortedDates[j]] ?? 0;
          movingAvg = sum / (i + 1);
        }
        return { date, count, movingAvg };
      });

      const predictive: PredictiveData = {
        monthProjectedRevenue,
        monthRevenueSoFar,
        daysElapsed,
        daysInMonth,
        appointmentsTrend,
      };

      return { comparative, predictive };
    },
    enabled: !!clinicId && !!dateFrom && !!dateTo,
  });
}
